import { useEffect, useState } from "react"

import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import { AuthSelectors } from "../Containers/LoginPage/redux/auth.slice"
import { UserSelectors } from "../Containers/User/redux/user.slice"
import API, { request } from "../Services/Api"
import { getAdminPostLoginRoute, isPublicAdminRoute } from "../Utils/adminTwoFactor"
import {
  authPaths,
  defaultInnerPage,
  innerPages,
  registrationPages,
  signUpPage
} from "../Utils/appRoutes"
import { isOnboardingFinished } from "../Utils/onboarding/isOnboardingFinished"
import utils, { createAccountStepPath, onboardingStepPath } from "../Utils/utils"

export const useStartupRedirect = () => {
  const [ready, setReady] = useState(false)
  const navigate = useNavigate()

  const userInfo = useSelector(AuthSelectors.getUserInfo)
  const nextCreateAccountStep = useSelector(UserSelectors.getNextCreateAccountStep)
  const accessToken = useSelector(AuthSelectors.getAccessToken)
  const refreshToken = useSelector(AuthSelectors.getRefreshToken)

  useEffect(() => {
    const run = async () => {
      try {
        const { pathname, search } = document.location
        const onboardingFinished = isOnboardingFinished(userInfo)

        if (accessToken) API.updateAccessToken(accessToken, refreshToken)

        // The MCP consent page owns its own auth handling (render when signed in, bounce to login
        // when not). Don't let startup redirect the user away to their default inner page.
        if (pathname.includes("/oauth/mcp/authorize")) {
          return
        }

        if (accessToken && userInfo) {
          const availablePaths = innerPages
          if (
            (utils.includeCurrentLocation(pathname, "sign-up") && pathname !== "/sign-up") ||
            utils.includeCurrentLocation(pathname, "success")
          ) {
            // Confirm-email links (/success/:hash, /api/success/:hash) must be allowed through
            // even for an already-authenticated-but-unverified user (the normal case right after
            // signup) - otherwise this redirect fires before CheckEmailCode ever mounts and the
            // hash never gets confirmed.
            // no-op
          } else if (
            pathname !== "/" &&
            availablePaths.some((el) => utils.includeCurrentLocation(pathname, el))
          ) {
            const nextUrl = search?.includes("?next=") ? search?.split("?next=")?.[1] : null
            if (nextUrl) {
              let decodedNextUrl
              try {
                decodedNextUrl = decodeURIComponent(nextUrl)
              } catch (e) {
                console.error("Error decoding nextUrl:", e)
                decodedNextUrl = nextUrl
              }

              const responseOauth = await request.api.post(decodedNextUrl, {
                maxRedirects: 0,
                validateStatus: (status) => status >= 200 && status < 400
              })

              if (responseOauth?.data?.redirect_uri) {
                utils.goLink(responseOauth?.data?.redirect_uri)
              } else {
                navigate(defaultInnerPage)
              }
            }
          } else if (
            onboardingFinished &&
            userInfo?.role?.name !== "Admin" &&
            userInfo["email_added"]
          ) {
            if (authPaths.some((path) => pathname.includes(path))) {
              // no-op
            } else if (nextCreateAccountStep) {
              if (pathname !== createAccountStepPath[nextCreateAccountStep])
                navigate(createAccountStepPath[nextCreateAccountStep] + (search || ""))
            } else {
              navigate(defaultInnerPage + (search || ""))
            }
          } else if (userInfo?.role?.name === "Admin") {
            if (isPublicAdminRoute(pathname)) {
              // Keep temporary 2FA pages accessible during setup.
            } else {
              navigate(getAdminPostLoginRoute(userInfo))
            }
          } else if (userInfo["email_added"] && !onboardingFinished) {
            if (!userInfo["is_email_verified"]) {
              navigate("/getting_started" + (search || ""))
            } else if (onboardingStepPath[userInfo?.next_step]) {
              if (authPaths.some((path) => pathname.includes(path))) {
                // no-op
              } else {
                navigate(onboardingStepPath[userInfo?.next_step] + (search || ""))
              }
            }
          } else {
            navigate("/add-email")
          }
        } else if (
          registrationPages.some((el) => utils.includeCurrentLocation(pathname, el)) ||
          signUpPage === pathname
        ) {
          if (pathname === "/sign-up") {
            navigate(`/sign-up${search || ""}`)
          } else if (pathname?.includes("/auth/")) {
            // no-op
          } else {
            // no-op
          }
        } else if (isPublicAdminRoute(pathname)) {
          // Allow direct access to temporary admin 2FA pages without auth during setup.
        } else {
          if (
            !["/", "/login"]?.includes(pathname) &&
            innerPages.some((el) => utils.includeCurrentLocation(pathname, el))
          ) {
            if (pathname?.includes("/sales-tracker/stores/store/")) {
              navigate("/login", { replace: true, state: { reference: "/sales-tracker/stores" } })
            } else if (pathname?.includes("/sales-tracker/products/product/")) {
              navigate("/login", { replace: true, state: { reference: "/sales-tracker/products" } })
            } else {
              navigate("/login", { replace: true, state: { reference: pathname + (search || "") } })
            }
          } else {
            navigate(pathname === "/" ? "/login" + (search || "") : pathname + (search || ""), {
              replace: true
            })
          }
        }
      } catch (error) {
        console.error("Startup redirect failed:", error)
      } finally {
        setReady(true)
      }
    }

    run()
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [])

  return ready
}
